import * as api from "./client.js";

// ---------------------------------------------------------------------------
// Frontend types (used by components — do not change)
// ---------------------------------------------------------------------------

export type RollingSubmissionStatus = "pending_review" | "publish" | "rejected";
export type ItemStatus = "publish" | "pending" | "pending_review" | "draft" | "trash";

export type TeamMember = {
  id: string;
  name: string;
  role: string;
  jobTitle: string;
  email: string;
  location: string;
  dateJoined: string | null;
  previousFirm: string | null;
  created: string | null;
  modified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

export type DepartedLawyer = {
  id: string;
  name: string;
  role: string;
  jobTitle: string;
  dateLeft: string | null;
  firmJoined: string | null;
  created: string | null;
  modified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

export type KeyClient = {
  id: string;
  companyName: string;
  industry: string;
  engagementDate: string | null;
  publishable: boolean;
  created: string | null;
  modified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

export type WorkHighlight = {
  id: string;
  client: string;
  summary: string;
  date: string | null;
  created: string | null;
  modified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

export type SubmissionVisibility = "public" | "private";

export type RollingSubmission = {
  id: string;
  companyUuid: string;
  firmName: string;
  firmRef: string;
  officeUuid: string | null;
  officeName: string | null;
  contactUuid: string | null;
  country: string | null;
  practiceArea: string;
  practiceAreaId: number | null;
  practiceGroup: string | null;
  tableName: string | null;
  region: string;
  regionId: number | null;
  publication: string;
  publicationId: number | null;
  status: RollingSubmissionStatus;
  /** Overall submission post status (publish/draft/pending/trash). */
  submissionStatus: ItemStatus;
  visibility: SubmissionVisibility;
  publishedAt: string | null;
  lastUpdated: string;
  pendingReviewCount: number;
  teamActive: TeamMember[];
  teamDeparted: DepartedLawyer[];
  keyClients: KeyClient[];
  workHighlights: WorkHighlight[];
};

export type RollingSubmissionsFilters = {
  limit?: number;
  offset?: number;
  publicationId?: number;
  practiceAreaId?: number;
  regionId?: number;
  status?: RollingSubmissionStatus;
  search?: string;
  hasPendingReview?: boolean;
};

export type RollingSubmissionsResponse = {
  submissions: RollingSubmission[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
};

export type RollingSubmissionResponse = {
  submission: RollingSubmission;
};

export type RollingSubmissionUpdatePayload = {
  submissionStatus: ItemStatus;
  visibility: SubmissionVisibility;
  publishedAt: string | null;
  teamActive: TeamMember[];
  teamDeparted: DepartedLawyer[];
  keyClients: KeyClient[];
  workHighlights: WorkHighlight[];
};

export type RollingSubmissionCreatePayload = {
  companyUuid: string;
  companyName?: string;
  firmRef?: string;
  officeUuid?: string;
  officeName?: string;
  publicationId: number;
  publicationName?: string;
  practiceAreaId: number;
  practiceAreaName?: string;
  countryId?: number;
  country?: string;
  contactUuid?: string;
  status?: string;
  teamActive: TeamMember[];
  teamDeparted: DepartedLawyer[];
  keyClients: KeyClient[];
  workHighlights: WorkHighlight[];
};

// ---------------------------------------------------------------------------
// Raw API types (match the actual submission-api response shapes)
// ---------------------------------------------------------------------------

type ApiPagination = {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

type ApiRollingSubmission = {
  id: string;
  companyUuid: string;
  firmRef: string;
  companyName: string;
  officeUuid: string | null;
  officeName: string;
  contactUuid: string | null;
  publicationId: number;
  publicationName: string;
  practiceAreaId: number;
  practiceAreaName: string;
  practiceAreaSlug: string;
  practiceGroupId: number;
  practiceGroupName: string;
  tableName?: string | null;
  region: string;
  country: string;
  countryId: number;
  approvalStatus: RollingSubmissionStatus;
  status: string;
  visibility?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  pendingReviewCount?: number;
};

type ApiClient = {
  id: string;
  clientCompanyName: string;
  clientIndustry: string;
  clientEngagementDate: string | null;
  clientPublish: "yes" | "no";
  clientCreated: string | null;
  clientModified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

type ApiTeamActive = {
  id: string;
  activeName: string;
  activeRole: string;
  activeJobTitle: string;
  activeEmail: string;
  activeLocation: string;
  activeJoined: string | null;
  activePreviousFirm: string | null;
  activeCreated: string | null;
  activeModified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

type ApiTeamDeparted = {
  id: string;
  departedName: string;
  departedRole: string;
  departedJobTitle: string;
  departedDate: string | null;
  departedFirmJoined: string | null;
  departedCreated: string | null;
  departedModified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

type ApiMatter = {
  id: string;
  matterCompanyName: string;
  matterSummary: string;
  matterDate: string | null;
  matterCreated: string | null;
  matterModified: string | null;
  modifiedBy: string | null;
  status: ItemStatus;
};

type ApiRollingSubmissionDetail = ApiRollingSubmission & {
  clients: ApiClient[];
  teamActive: ApiTeamActive[];
  teamDeparted: ApiTeamDeparted[];
  matters: ApiMatter[];
};

type ApiListResponse = {
  rollingSubmissions: ApiRollingSubmission[];
  pagination: ApiPagination;
};

type ApiDetailResponse = {
  rollingSubmission: ApiRollingSubmissionDetail;
};

type ApiApproveResponse = {
  rollingSubmission: ApiRollingSubmissionDetail;
};

type ApiUpdateResponse = {
  rollingSubmission: ApiRollingSubmissionDetail;
};

// ---------------------------------------------------------------------------
// Mapper functions (API shape → frontend shape)
// ---------------------------------------------------------------------------

function mapApiSubmission(raw: ApiRollingSubmission): RollingSubmission {
  return {
    id: raw.id,
    companyUuid: raw.companyUuid,
    firmName: raw.companyName,
    firmRef: raw.firmRef,
    officeUuid: raw.officeUuid ?? null,
    officeName: raw.officeName,
    contactUuid: raw.contactUuid ?? null,
    country: raw.country,
    practiceArea: raw.practiceAreaName,
    practiceAreaId: raw.practiceAreaId,
    practiceGroup: raw.practiceGroupName ?? null,
    tableName: raw.tableName ?? null,
    region: raw.region,
    regionId: raw.countryId,
    publication: raw.publicationName,
    publicationId: raw.publicationId,
    status: raw.approvalStatus,
    submissionStatus: normaliseItemStatus(raw.status),
    visibility: raw.visibility === "private" ? "private" : "public",
    publishedAt: raw.publishedAt ?? null,
    lastUpdated: raw.updatedAt,
    pendingReviewCount: raw.pendingReviewCount ?? 0,
    teamActive: [],
    teamDeparted: [],
    keyClients: [],
    workHighlights: [],
  };
}

function normaliseItemStatus(value: string): ItemStatus {
  if (value === "pending_review") return "pending";
  if (value === "pending" || value === "draft" || value === "trash") return value;
  return "publish";
}

function mapApiClient(raw: ApiClient): KeyClient {
  return {
    id: raw.id,
    companyName: raw.clientCompanyName,
    industry: raw.clientIndustry,
    engagementDate: raw.clientEngagementDate,
    publishable: raw.clientPublish === "yes",
    created: raw.clientCreated,
    modified: raw.clientModified,
    modifiedBy: raw.modifiedBy,
    status: normaliseItemStatus(raw.status),
  };
}

function mapApiTeamActive(raw: ApiTeamActive): TeamMember {
  return {
    id: raw.id,
    name: raw.activeName,
    role: raw.activeRole,
    jobTitle: raw.activeJobTitle,
    email: raw.activeEmail,
    location: raw.activeLocation,
    dateJoined: raw.activeJoined,
    previousFirm: raw.activePreviousFirm,
    created: raw.activeCreated,
    modified: raw.activeModified,
    modifiedBy: raw.modifiedBy,
    status: normaliseItemStatus(raw.status),
  };
}

function mapApiTeamDeparted(raw: ApiTeamDeparted): DepartedLawyer {
  return {
    id: raw.id,
    name: raw.departedName,
    role: raw.departedRole,
    jobTitle: raw.departedJobTitle,
    dateLeft: raw.departedDate,
    firmJoined: raw.departedFirmJoined,
    created: raw.departedCreated,
    modified: raw.departedModified,
    modifiedBy: raw.modifiedBy,
    status: normaliseItemStatus(raw.status),
  };
}

function mapApiMatter(raw: ApiMatter): WorkHighlight {
  return {
    id: raw.id,
    client: raw.matterCompanyName,
    summary: raw.matterSummary,
    date: raw.matterDate,
    created: raw.matterCreated,
    modified: raw.matterModified,
    modifiedBy: raw.modifiedBy,
    status: normaliseItemStatus(raw.status),
  };
}

function mapApiSubmissionWithChildren(raw: ApiRollingSubmissionDetail): RollingSubmission {
  return {
    ...mapApiSubmission(raw),
    teamActive: raw.teamActive.map(mapApiTeamActive),
    teamDeparted: raw.teamDeparted.map(mapApiTeamDeparted),
    keyClients: raw.clients.map(mapApiClient),
    workHighlights: raw.matters.map(mapApiMatter),
  };
}

// ---------------------------------------------------------------------------
// Service (public API consumed by components)
// ---------------------------------------------------------------------------

export const rollingSubmissionsService = {
  getAll: async (filters?: RollingSubmissionsFilters): Promise<RollingSubmissionsResponse> => {
    const params = new URLSearchParams();
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());
    if (filters?.publicationId !== undefined)
      params.append("publicationId", filters.publicationId.toString());
    if (filters?.practiceAreaId !== undefined)
      params.append("practiceAreaId", filters.practiceAreaId.toString());
    if (filters?.regionId !== undefined) params.append("countryId", filters.regionId.toString());
    if (filters?.status) params.append("approvalStatus", filters.status);
    if (filters?.hasPendingReview) params.append("hasPendingReview", "true");
    if (filters?.search) params.append("search", filters.search);

    const query = params.toString() ? `?${params.toString()}` : "";
    const raw = await api.get<ApiListResponse>(`/v1/submission/rolling-submissions${query}`);

    return {
      submissions: raw.rollingSubmissions.map(mapApiSubmission),
      pagination: raw.pagination,
    };
  },

  getById: async (id: string): Promise<RollingSubmissionResponse> => {
    const raw = await api.get<ApiDetailResponse>(`/v1/submission/rolling-submissions/${id}`);

    return {
      submission: mapApiSubmissionWithChildren(raw.rollingSubmission),
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/submission/rolling-submissions/${id}`);
  },

  approve: async (
    id: string,
    status: RollingSubmissionStatus,
  ): Promise<RollingSubmissionResponse> => {
    const raw = await api.patch<ApiApproveResponse>(
      `/v1/submission/rolling-submissions/${id}/approve`,
      { approvalStatus: status },
    );

    return {
      submission: mapApiSubmissionWithChildren(raw.rollingSubmission),
    };
  },

  update: async (
    id: string,
    data: RollingSubmissionUpdatePayload,
  ): Promise<RollingSubmissionResponse> => {
    const apiPayload = {
      status: data.submissionStatus,
      visibility: data.visibility,
      publishedAt: data.publishedAt,
      teamActive: data.teamActive.map((m, i) => ({
        position: i,
        activeName: m.name,
        activeRole: m.role,
        activeJobTitle: m.jobTitle,
        activeEmail: m.email,
        activeLocation: m.location,
        activeJoined: m.dateJoined,
        activePreviousFirm: m.previousFirm,
        activeCreated: m.created,
        status: m.status,
      })),
      teamDeparted: data.teamDeparted.map((d, i) => ({
        position: i,
        departedName: d.name,
        departedRole: d.role,
        departedJobTitle: d.jobTitle,
        departedDate: d.dateLeft,
        departedFirmJoined: d.firmJoined,
        departedCreated: d.created,
        status: d.status,
      })),
      clients: data.keyClients.map((c, i) => ({
        position: i,
        clientCompanyName: c.companyName,
        clientIndustry: c.industry,
        clientEngagementDate: c.engagementDate,
        clientPublish: c.publishable ? "yes" : "no",
        clientCreated: c.created,
        status: c.status,
      })),
      matters: data.workHighlights.map((w, i) => ({
        position: i,
        matterCompanyName: w.client,
        matterSummary: w.summary,
        matterDate: w.date,
        matterCreated: w.created,
        status: w.status,
      })),
    };

    const raw = await api.put<ApiUpdateResponse>(
      `/v1/submission/rolling-submissions/${id}`,
      apiPayload,
    );

    return {
      submission: mapApiSubmissionWithChildren(raw.rollingSubmission),
    };
  },

  create: async (data: RollingSubmissionCreatePayload): Promise<RollingSubmissionResponse> => {
    const apiPayload = {
      companyUuid: data.companyUuid,
      companyName: data.companyName ?? null,
      firmRef: data.firmRef ?? null,
      officeUuid: data.officeUuid ?? null,
      officeName: data.officeName ?? null,
      publicationId: data.publicationId,
      publicationName: data.publicationName ?? null,
      practiceAreaId: data.practiceAreaId,
      practiceAreaName: data.practiceAreaName ?? null,
      editorialCategoryId: data.practiceAreaId,
      countryId: data.countryId ?? null,
      country: data.country ?? null,
      contactUuid: data.contactUuid ?? null,
      status: data.status ?? "publish",
      teamActive: data.teamActive.map((m, i) => ({
        position: i,
        activeName: m.name,
        activeRole: m.role,
        activeJobTitle: m.jobTitle,
        activeEmail: m.email,
        activeLocation: m.location,
        activeJoined: m.dateJoined,
        activePreviousFirm: m.previousFirm,
        status: m.status,
      })),
      teamDeparted: data.teamDeparted.map((d, i) => ({
        position: i,
        departedName: d.name,
        departedRole: d.role,
        departedJobTitle: d.jobTitle,
        departedDate: d.dateLeft,
        departedFirmJoined: d.firmJoined,
        status: d.status,
      })),
      clients: data.keyClients.map((c, i) => ({
        position: i,
        clientCompanyName: c.companyName,
        clientIndustry: c.industry,
        clientEngagementDate: c.engagementDate,
        clientPublish: c.publishable ? "yes" : "no",
        status: c.status,
      })),
      matters: data.workHighlights.map((w, i) => ({
        position: i,
        matterCompanyName: w.client,
        matterSummary: w.summary,
        matterDate: w.date,
        status: w.status,
      })),
    };

    const raw = await api.post<ApiUpdateResponse>("/v1/submission/rolling-submissions", apiPayload);

    return {
      submission: mapApiSubmissionWithChildren(raw.rollingSubmission),
    };
  },
};
